import { useCallback, useState } from 'react'

type ColumnDef<Row = unknown> = {
  key: string
  label?: React.ReactNode
  isSortable?: boolean
  sortKey?: string
  sortFn?: (a: Row, b: Row) => number
  headerClassName?: string
  width?: number // Now represents percentage (0-100)
  minWidth?: number // Now represents percentage (0-100)
  maxWidth?: number // Now represents percentage (0-100)
}

type Params<Row> = {
  columns: ColumnDef<Row>[]
  enableColumnResize?: boolean
}

export function useColumnResize<Row>({
  columns,
  enableColumnResize,
}: Params<Row>) {
  const [columnWidths, setColumnWidths] = useState<(number | null)[]>(() => {
    // If no explicit widths, distribute evenly among columns
    if (columns.every((c) => !c.width)) {
      const evenWidth = 100 / columns.length
      return columns.map(() => evenWidth)
    }

    // If some columns have explicit widths, calculate remaining space for others
    const explicitWidths = columns.map((c) => c.width ?? null)
    const totalExplicitWidth = explicitWidths.reduce(
      (sum, width) => sum + (width ?? 0),
      0
    )
    const remainingSpace = 100 - totalExplicitWidth
    const columnsWithoutWidth = explicitWidths.filter(
      (width) => width === null
    ).length

    if (columnsWithoutWidth > 0) {
      const remainingWidthPerColumn = remainingSpace / columnsWithoutWidth
      return explicitWidths.map((width) => width ?? remainingWidthPerColumn)
    }

    return explicitWidths
  })

  const handleStartResize = useCallback(
    (columnIndex: number, event: MouseEvent | React.MouseEvent) => {
      if (!enableColumnResize) return

      const startX = (event as MouseEvent).clientX
      const startWidths = [...columnWidths]

      // Get the table element to calculate percentage changes
      const tableElement = (event.target as HTMLElement).closest('table')
      if (!tableElement) return

      const tableWidth = tableElement.getBoundingClientRect().width

      // Ensure all columns have a width (in percentages)
      startWidths.forEach((width, index) => {
        const defaultWidth = 100 / columns.length // Default even distribution
        if (!width) {
          startWidths[index] = defaultWidth
        }
      })

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - startX
        const deltaPercentage = (deltaX / tableWidth) * 100 // Convert pixel delta to percentage

        const originalWidth = startWidths[columnIndex]
        const minWidth = Math.max(5, columns[columnIndex]?.minWidth ?? 0) // Minimum 5%
        const maxWidthFromCol = columns[columnIndex]?.maxWidth
        const requestedWidthBase = Math.max(
          minWidth,
          originalWidth + deltaPercentage
        )

        // Constrain the new width
        const requestedWidth =
          typeof maxWidthFromCol === 'number'
            ? Math.min(requestedWidthBase, maxWidthFromCol)
            : requestedWidthBase
        const newWidth = Math.max(minWidth, requestedWidth)
        const actualDelta = newWidth - originalWidth

        if (Math.abs(actualDelta) < 0.1) return // Threshold for percentage changes

        // Find all other columns that need to be adjusted
        const otherColumns: number[] = []
        for (let i = 0; i < startWidths.length; i++) {
          if (i !== columnIndex) {
            otherColumns.push(i)
          }
        }

        // Calculate how much we need to distribute among other columns
        const redistributeAmount = -actualDelta

        // Start with current widths
        const newWidths = [...startWidths]
        newWidths[columnIndex] = newWidth

        // Distribute the redistributeAmount proportionally among other columns
        // but respect minimum widths
        let remainingToDistribute = redistributeAmount

        // First pass: distribute proportionally
        const otherColumnsTotalWidth = otherColumns.reduce(
          (sum, i) => sum + startWidths[i],
          0
        )

        for (const otherIndex of otherColumns) {
          if (otherColumnsTotalWidth === 0) break

          const proportion = startWidths[otherIndex] / otherColumnsTotalWidth
          const adjustment = redistributeAmount * proportion
          const proposedWidth = startWidths[otherIndex] + adjustment
          const minW = Math.max(5, columns[otherIndex]?.minWidth ?? 0) // Minimum 5%
          const constrainedWidth = Math.max(minW, proposedWidth)

          newWidths[otherIndex] = constrainedWidth
          remainingToDistribute -= constrainedWidth - startWidths[otherIndex]
        }

        // Second pass: if there's still remaining amount due to min width constraints,
        // distribute it among columns that can still shrink
        if (Math.abs(remainingToDistribute) > 0.1) {
          const flexibleColumns = otherColumns.filter((i) => {
            const minW = Math.max(5, columns[i]?.minWidth ?? 0)
            return newWidths[i] > minW
          })
          if (flexibleColumns.length > 0) {
            const extraAdjustment =
              remainingToDistribute / flexibleColumns.length
            for (const flexIndex of flexibleColumns) {
              const currentWidth = newWidths[flexIndex]
              const minW = Math.max(5, columns[flexIndex]?.minWidth ?? 0)
              newWidths[flexIndex] = Math.max(
                minW,
                currentWidth + extraAdjustment
              )
            }
          }
        }

        // Ensure total width stays at 100%
        const totalWidth = newWidths.reduce((sum, width) => sum + width, 0)
        if (Math.abs(totalWidth - 100) > 0.1) {
          // Normalize to 100%
          const factor = 100 / totalWidth
          for (let i = 0; i < newWidths.length; i++) {
            newWidths[i] = newWidths[i] * factor
          }
        }

        setColumnWidths(newWidths)
      }

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [columns, columnWidths, enableColumnResize]
  )

  return { columnWidths, handleStartResize }
}
